import { sql, sqlRaw } from '../../../base';
import { getTasksSchemaInfo, resetTasksSchemaCache } from '../../../schemaCache';
import type { Task } from '../../../types';
import type { TasksSchemaInfo } from '../../../schemaCache';

interface UpdateContext {
  id: string;
  userId: string;
  data: Partial<Task>;
  schema: TasksSchemaInfo;
  normalizedTimeSpent: number | null;
  normalizedTimeSpentMinutes: number | null;
}

type TemplateTuple = TemplateStringsArray & { raw: readonly string[] };

function createTemplate(strings: string[], values: any[]): TemplateTuple {
  const template = strings as unknown as TemplateTuple;
  template.raw = strings;
  return template;
}

function appendCoalesce(
  strings: string[],
  values: any[],
  state: { prefix: string },
  column: string,
  value: any,
  fallbackColumn?: string
) {
  strings[strings.length - 1] += `${state.prefix}${column} = COALESCE(`;
  values.push(value);
  strings.push(`, ${fallbackColumn ?? column})`);
  strings.push('');
  state.prefix = ', ';
}

function appendCaseForSop(strings: string[], values: any[], state: { prefix: string }, value: any) {
  strings[strings.length - 1] += `${state.prefix}sop = CASE WHEN `;
  values.push(value === undefined);
  strings.push(' THEN sop ELSE ');
  values.push(value ?? null);
  strings.push(' END');
  strings.push('');
  state.prefix = ', ';
}

async function runDependencyUpdate(context: UpdateContext): Promise<Task> {
  // Build a safe, parameterized UPDATE dynamically using explicit $n placeholders
  const { id, userId, data, schema, normalizedTimeSpent, normalizedTimeSpentMinutes } = context;
  const sets: string[] = [];
  const values: any[] = [];
  let i = 0;

  const pushCoalesce = (column: string, value: any, fallbackColumn?: string) => {
    values.push(value);
    i += 1;
    sets.push(`${column} = COALESCE($${i}, ${fallbackColumn ?? column})`);
  };

  pushCoalesce('title', data.title);
  pushCoalesce('description', data.description);
  pushCoalesce('priority', data.priority);
  pushCoalesce('status', data.status);
  pushCoalesce('task_type', data.task_type);
  pushCoalesce('assigned_to', data.assigned_to);
  pushCoalesce('deadline', data.deadline);
  pushCoalesce('start_date', data.start_date);
  pushCoalesce('end_date', data.end_date);
  pushCoalesce('position', data.position);

  if (schema.hasRegular) {
    pushCoalesce('is_regular', data.is_regular ?? data.is_daily, 'is_regular');
  } else if (schema.hasDaily) {
    pushCoalesce('is_daily', data.is_daily, 'is_daily');
  }

  if (schema.hasFrequency) pushCoalesce('frequency', data.frequency);
  if (schema.hasNextDueDate) pushCoalesce('next_due_date', data.next_due_date);
  if (schema.hasRecurrenceDay) pushCoalesce('recurrence_day', data.recurrence_day);

  // Dependency columns were Postgres-specific; in D1 we store edges in task_dependencies.
  // If depends_on is provided, we will update edges separately below.
  if (schema.hasDependencyColumns) {
    pushCoalesce('is_live', data.is_live);
    pushCoalesce('dependency_type', data.dependency_type);
    pushCoalesce('dependency_group', data.dependency_group);
  }

  if (schema.hasSOPColumn) {
    // sop = CASE WHEN $n THEN sop ELSE $n+1 END
    values.push(data.sop === undefined);
    i += 1;
    values.push(data.sop ?? null);
    i += 1;
    sets.push(`sop = CASE WHEN $${i - 1} THEN sop ELSE $${i} END`);
  }

  if (schema.hasTimeSpent) pushCoalesce('time_spent', normalizedTimeSpent, 'time_spent');
  if (schema.hasTimeSpentMinutes) pushCoalesce('time_spent_minutes', normalizedTimeSpentMinutes, 'time_spent_minutes');
  if (schema.hasNotes) pushCoalesce('notes', data.notes);

  // WHERE clause with ownership constraint
  values.push(id); i += 1;
  values.push(userId); i += 1;
  values.push(userId); i += 1;
  const query = `UPDATE tasks SET ${sets.join(', ')} WHERE id = $${i - 2} AND (user_id = $${i - 1} OR assigned_to = $${i}) RETURNING *`;

  const result = await sqlRaw(query, values);
  if (!result || result.length === 0) {
    throw new Error('No task was updated - task may not exist or user does not have permission');
  }
  if (data.status === 'Complete' && result[0].status !== 'Complete') {
    throw new Error('Task update failed - status was not changed to Complete');
  }
  const updated = result[0] as Task;
  // If dependencies were provided, rewrite edges and recompute is_live
  if (Array.isArray(data.depends_on)) {
    await sqlRaw('DELETE FROM task_dependencies WHERE task_id = $1', [id]);
    if (data.depends_on.length > 0) {
      const depValues: any[] = [];
      const tuples = data.depends_on.map((depId, idx) => {
        depValues.push(id, depId);
        const a = idx * 2 + 1;
        const b = idx * 2 + 2;
        return `($${a}, $${b})`;
      }).join(', ');
      await sqlRaw(`INSERT INTO task_dependencies (task_id, depends_on_task_id) VALUES ${tuples}`, depValues);
    }
    await sqlRaw(
      `UPDATE tasks AS t
       SET is_live = (
         CASE WHEN NOT EXISTS (SELECT 1 FROM task_dependencies td WHERE td.task_id = t.id)
              THEN 1
              ELSE (
                CASE WHEN NOT EXISTS (
                         SELECT 1 FROM task_dependencies td
                         JOIN tasks d ON d.id = td.depends_on_task_id
                         WHERE td.task_id = t.id AND (d.status IS NULL OR d.status != 'Complete')
                     ) THEN 1 ELSE 0 END
              )
         END
       ), updated_at = CURRENT_TIMESTAMP
       WHERE t.id = $1`,
      [id]
    );
  }
  return updated;
}

async function runLegacyUpdate(context: UpdateContext): Promise<Task> {
  const { id, userId, data, schema, normalizedTimeSpent, normalizedTimeSpentMinutes } = context;
  const sets: string[] = [];
  const values: any[] = [];
  let i = 0;

  const pushCoalesce = (column: string, value: any, fallbackColumn?: string) => {
    values.push(value);
    i += 1;
    sets.push(`${column} = COALESCE($${i}, ${fallbackColumn ?? column})`);
  };

  pushCoalesce('title', data.title);
  pushCoalesce('description', data.description);
  pushCoalesce('priority', data.priority);
  pushCoalesce('status', data.status);
  pushCoalesce('assigned_to', data.assigned_to);
  pushCoalesce('deadline', data.deadline);
  pushCoalesce('start_date', data.start_date);
  pushCoalesce('end_date', data.end_date);
  pushCoalesce('position', data.position);

  if (schema.hasRegular) {
    pushCoalesce('is_regular', data.is_regular ?? data.is_daily, 'is_regular');
  } else if (schema.hasDaily) {
    pushCoalesce('is_daily', data.is_daily, 'is_daily');
  }
  if (schema.hasFrequency) pushCoalesce('frequency', data.frequency);
  if (schema.hasNextDueDate) pushCoalesce('next_due_date', data.next_due_date);
  if (schema.hasRecurrenceDay) pushCoalesce('recurrence_day', data.recurrence_day);
  if (schema.hasTimeSpent) pushCoalesce('time_spent', normalizedTimeSpent, 'time_spent');
  if (schema.hasTimeSpentMinutes) pushCoalesce('time_spent_minutes', normalizedTimeSpentMinutes, 'time_spent_minutes');
  if (schema.hasNotes) pushCoalesce('notes', data.notes);

  // WHERE clause with ownership constraint
  values.push(id); i += 1;
  values.push(userId); i += 1;
  values.push(userId); i += 1;
  const query = `UPDATE tasks SET ${sets.join(', ')} WHERE id = $${i - 2} AND (user_id = $${i - 1} OR assigned_to = $${i}) RETURNING *`;

  const result = await sqlRaw(query, values);
  if (!result || result.length === 0) {
    throw new Error('No task was updated - task may not exist or user does not have permission');
  }
  return result[0] as Task;
}

export const updateTaskMutations = {
  async update(id: string, userId: string, data: Partial<Task>): Promise<Task> {
    try {
      const currentTask = await sql`
        SELECT * FROM tasks
        WHERE id = ${id}
        AND (user_id = ${userId} OR assigned_to = ${userId})
      `;

      if (!currentTask || currentTask.length === 0) {
        throw new Error('Task not found or user does not have permission');
      }

      const schema = await getTasksSchemaInfo();
      const normalizedTimeSpent = data.time_spent ?? data.time_spent_minutes ?? null;
      const normalizedTimeSpentMinutes = data.time_spent_minutes ?? data.time_spent ?? null;

      const context: UpdateContext = {
        id,
        userId,
        data,
        schema,
        normalizedTimeSpent,
        normalizedTimeSpentMinutes
      };

      const updatedTask = schema.hasDependencyColumns
        ? await runDependencyUpdate(context)
        : await runLegacyUpdate(context);

      if (data.notes === undefined) {
        return updatedTask;
      }

      try {
        const notesResult = await sql`
          UPDATE tasks
          SET notes = ${data.notes},
              updated_at = now()
          WHERE id = ${id} AND (user_id = ${userId} OR assigned_to = ${userId})
          RETURNING *
        `;

        if (notesResult && notesResult.length > 0) {
          return notesResult[0] as Task;
        }

        return { ...updatedTask, notes: data.notes ?? null };
      } catch (error: any) {
        if (typeof error?.message === 'string' && error.message.includes('column "notes"')) {
          console.warn('[db.tasks.update] notes column missing; skipping deferred marker update.');
          resetTasksSchemaCache();
          return updatedTask;
        }
        throw error;
      }
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }
};
